/**
 * What both tills get back from a search (`/pharmacy/till/items`,
 * `/shop/items`) and how they label it. One shape and one set of labels, so an
 * item reads the same at the pharmacy till and the shop till.
 */

import { TILL_ITEM_STATE_LABELS, type TillItemState } from '@cenox/shared';

export interface TillUnit {
  id: string;
  name: string;
  baseQuantity: number;
  barcode: string | null;
  price: string | null;
  isBase: boolean;
  sellable: boolean;
}

export interface TillItem {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  categoryName: string | null;
  genericName: string | null;
  strength: string | null;
  dosageForm: string | null;
  rxClass: string | null;
  isMedicine: boolean | null;
  tracksBatches: boolean;
  price: string | null;
  saleableOnHand: number | null;
  nearestExpiry: string | null;
  state: TillItemState;
  sellable: boolean;
  reason: string | null;
  units: TillUnit[];
  matchedUnitId: string | null;
}

export const tillStateLabel = (state: TillItemState): string => TILL_ITEM_STATE_LABELS[state];

export function tillStateTone(state: TillItemState): 'success' | 'warning' | 'danger' | 'neutral' | 'info' {
  switch (state) {
    case 'available':
      return 'success';
    case 'out_of_stock':
      return 'danger';
    case 'discontinued':
      return 'neutral';
    case 'pharmacy_till_only':
      return 'info';
    default:
      return 'warning';
  }
}

/** The unit Enter or a single tap adds: the scanned pack, else the base unit, else the first one on sale. */
export function defaultUnit(item: TillItem): TillUnit | null {
  const onSale = item.units.filter((u) => u.sellable && u.price !== null);
  return (
    onSale.find((u) => u.id === item.matchedUnitId) ??
    onSale.find((u) => u.isBase) ??
    onSale[0] ??
    null
  );
}

/**
 * In a pharmacy, money other than cash counts once somebody has seen it
 * arrive, and a transfer names the reference on its bank alert — which pays
 * once. The server holds the rule (sales/paymentChecks.ts); these let a screen
 * ask for the tick and the reference before it is refused.
 */
const CONFIRMED_ON_SIGHT = new Set(['card', 'bank_transfer', 'pos_terminal', 'mobile_money', 'cheque', 'other']);
const PROVED_BY_REFERENCE = new Set(['bank_transfer', 'mobile_money']);

export const needsConfirmation = (method: string): boolean => CONFIRMED_ON_SIGHT.has(method);
export const needsTransferReference = (method: string): boolean => PROVED_BY_REFERENCE.has(method);

/**
 * What a typed reference is, said where it is typed. CenoX refuses a
 * reference that has already paid; it cannot see the bank, so the transfer
 * still waits to be matched against the statement (Pharmacy → Finance →
 * Bank transfers).
 */
export const TRANSFER_REFERENCE_HINT =
  'CenoX only checks this reference has not paid before. It does not prove the money arrived — transfers are matched against the bank statement later.';
/** Long enough to be a bank's reference once spacing and dashes are ignored. */
export const transferReferenceReady = (reference: string): boolean =>
  reference.toUpperCase().replace(/[^A-Z0-9]/g, '').length >= 6;
